import React, { Fragment, Component } from 'react';
import { motion } from 'framer-motion';
import { Film, Gamepad2, Tv, Award, Star, Ticket } from 'lucide-react';
import { Eyebrow } from './UI';
type Feature = {
  image: string;
  title: string;
  subtitle: string;
  type: string;
  platform: string;
  year: string;
  rating: string;
  runtime: string;
  Icon: ComponentType<{
    size?: number;
    strokeWidth?: number;
    className?: string;
  }>;
};
const features: Feature[] = [{
  image: 'https://cdn.magicpatterns.com/uploads/5taRZeiHKbeHUgT9mvbj8f/hf_20260519_141137_57b95531-a597-4707-a3ba-98dbd149b0ec.png',
  title: 'CALL OF DUTY',
  subtitle: 'Simon "Ghost" Riley',
  type: 'VIDEO GAME · ACTION',
  platform: 'ACTIVISION',
  year: 'FRANCHISE',
  rating: '17+',
  runtime: 'AAA TITLE',
  Icon: Gamepad2
}, {
  image: 'https://cdn.magicpatterns.com/uploads/3snzvUnWRWmgNfKZ25UMLW/hf_20260519_141536_43f5116e-4a0d-41b9-adcf-ee03778920ab.png',
  title: 'THE TOWN',
  subtitle: 'Ben Affleck · Jeremy Renner',
  type: 'CRIME · THRILLER',
  platform: 'HBO MAX',
  year: '2010',
  rating: 'R',
  runtime: '125 MIN',
  Icon: Film
}, {
  image: 'https://cdn.magicpatterns.com/uploads/6aBwpUozKskfcjRf6H5Pjr/hf_20260519_142247_77b42ac5-d266-4fdb-aba9-4088926a7447.png',
  title: 'MARAUDERS',
  subtitle: 'Bruce Willis · Dave Bautista',
  type: 'ACTION · CRIME',
  platform: 'AMAZON PRIME',
  year: '2016',
  rating: 'R',
  runtime: '107 MIN',
  Icon: Film
}, {
  image: 'https://cdn.magicpatterns.com/uploads/nLLHhr5CSE1oEBGbKrjhSY/hf_20260519_142513_f490f85f-18eb-4985-9094-d1e29583a797.png',
  title: 'FIELD OPS',
  subtitle: 'Real Tactical Deployment',
  type: 'OPERATOR · CLASSIFIED',
  platform: 'CLASSIFIED',
  year: 'ACTIVE',
  rating: 'XX',
  runtime: 'LIVE',
  Icon: Award
}];
const platformLogos = ['NETFLIX', 'HBO MAX', 'AMAZON PRIME', 'ACTIVISION', 'WARNER BROS', 'LIONSGATE'];
export const AsSeenIn = () => {
  return <section className="relative bg-base-black py-24 overflow-hidden border-y border-dark-3" data-id="element-1">
      {/* Background grid */}
      <div className="absolute inset-0 bg-tactical-grid opacity-[0.04] pointer-events-none" data-id="element-2"></div>

      {/* Top scanline accent */}
      <div className="absolute top-0 left-0 w-full h-[1px] bg-gradient-to-r from-transparent via-acid-green/40 to-transparent" data-id="element-3"></div>

      <div className="container mx-auto px-6 md:px-12 relative z-10" data-id="element-4">
        {/* Header */}
        <div className="text-center mb-14" data-id="element-5">
          <Eyebrow data-id="element-6">POP CULTURE DEPLOYMENT</Eyebrow>
          <h2 className="text-5xl md:text-[64px] leading-[0.9] mb-5 font-bebas text-white tracking-tight" data-id="element-7">
            AS SEEN IN HOLLYWOOD,{' '}
            <span className="text-acid-green" data-id="element-8">STREAMING & GAMING.</span>
          </h2>
          <p className="font-barlow text-lg text-text-body max-w-2xl mx-auto" data-id="element-9">
            The same skull-face silhouette featured in major motion pictures,
            AAA video games, and tactical loadouts worldwide.
          </p>
        </div>

        {/* Platform Marquee Strip — Auto-scrolling infinite loop */}
        <div className="border-y border-dark-3 bg-dark-1/40 mb-12 overflow-hidden relative" data-id="element-10">
          {/* Edge fade gradients */}
          <div className="absolute left-0 top-0 bottom-0 w-20 bg-gradient-to-r from-base-black to-transparent z-10 pointer-events-none" data-id="element-11"></div>
          <div className="absolute right-0 top-0 bottom-0 w-20 bg-gradient-to-l from-base-black to-transparent z-10 pointer-events-none" data-id="element-12"></div>

          <motion.div className="flex items-center gap-10 py-4 whitespace-nowrap" animate={{
          x: ['0%', '-50%']
        }} transition={{
          duration: 12,
          ease: 'linear',
          repeat: Infinity
        }} data-id="element-13">
            {/* Render the list twice for seamless infinite loop */}
            {[...platformLogos, ...platformLogos].map((platform, i) => <Fragment key={i} data-id="element-14">
                <span className="font-bebas text-sm md:text-base tracking-[4px] text-text-dimmed hover:text-acid-green transition-colors shrink-0" data-id="element-15">
                  {platform}
                </span>
                <span className="text-acid-green/40 shrink-0 text-xs" data-id="element-16">●</span>
              </Fragment>)}
          </motion.div>
        </div>

        {/* Featured Productions Grid — Refined Tactical Dossier Cards */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 md:gap-5" data-id="element-17">
          {features.map((f, i) => <motion.div key={i} initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true,
          margin: '-50px'
        }} transition={{
          duration: 0.5,
          delay: i * 0.08,
          ease: 'easeOut'
        }} className="group relative bg-dark-1 border border-dark-3 hover:border-acid-green/40 transition-all duration-300 overflow-hidden" data-id="element-18">
              {/* Image — compact 3:4 aspect ratio */}
              <div className="relative aspect-[3/4] overflow-hidden bg-base-black" data-id="element-19">
                <img src={f.image} alt={f.title} className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-[1.04]" data-id="element-20" />

                {/* Subtle bottom gradient for text legibility */}
                <div className="absolute inset-0 bg-gradient-to-t from-base-black via-base-black/10 to-transparent pointer-events-none" data-id="element-21"></div>

                {/* Refined platform badge — top right */}
                <div className="absolute top-3 right-3 z-10" data-id="element-22">
                  <span className="font-barlow font-bold text-[9px] text-acid-green tracking-[3px] uppercase bg-base-black/80 backdrop-blur-sm border border-acid-green/30 px-2 py-1" data-id="element-23">
                    {f.platform}
                  </span>
                </div>

                {/* Index number — top left, minimalist */}
                <div className="absolute top-3 left-3 z-10 font-bebas text-xs text-white/70 tracking-[3px]" data-id="element-24">
                  {String(i + 1).padStart(2, '0')} / 04
                </div>
              </div>

              {/* Info Strip — Clean tactical metadata */}
              <div className="p-4 border-t border-dark-3 bg-dark-1" data-id="element-25">
                {/* Type */}
                <div className="font-barlow font-bold text-[9px] text-acid-green tracking-[3px] uppercase mb-2" data-id="element-26">
                  {f.type}
                </div>

                {/* Title */}
                <h3 className="font-bebas text-xl md:text-2xl text-white leading-none mb-1.5 tracking-wide" data-id="element-27">
                  {f.title}
                </h3>

                {/* Cast */}
                <p className="font-inter text-[11px] text-text-dimmed leading-snug truncate" data-id="element-28">
                  {f.subtitle}
                </p>
              </div>

              {/* Subtle bottom accent line on hover */}
              <div className="absolute bottom-0 left-0 h-[2px] bg-acid-green w-0 group-hover:w-full transition-all duration-500 ease-out" data-id="element-29"></div>
            </motion.div>)}
        </div>

        {/* Footer Note */}
        <div className="mt-12 max-w-3xl mx-auto" data-id="element-30">
          <div className="border border-dark-3 bg-dark-1/50 backdrop-blur-sm p-5 flex items-start gap-4" data-id="element-31">
            <div className="shrink-0 w-9 h-9 border border-acid-green/40 bg-acid-green/5 flex items-center justify-center text-acid-green" data-id="element-32">
              <Tv size={16} strokeWidth={2} data-id="element-33" />
            </div>
            <div data-id="element-34">
              <div className="font-barlow font-bold text-[10px] text-acid-green tracking-[3px] uppercase mb-1" data-id="element-35">
                INTEL NOTE
              </div>
              <p className="font-inter text-[13px] text-text-body leading-relaxed" data-id="element-36">
                The iconic skull-face mask design has become a cultural symbol —
                from Simon "Ghost" Riley in{' '}
                <span className="text-white font-medium" data-id="element-37">Call of Duty</span>, to
                the heist crews of{' '}
                <span className="text-white font-medium" data-id="element-38">The Town</span> and{' '}
                <span className="text-white font-medium" data-id="element-39">Marauders</span>, to
                tactical enthusiasts everywhere. Now, you own the version
                actually built to perform.
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>;
};